import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail, Github, Send, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { profileData } from '../utils/mockData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });

  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    try {
      await axios.post(`${API}/contact`, formData);
      setStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setStatus('idle'), 4000);
    } catch (err) {
      setStatus('error');
      setErrorMsg(
        err.response?.data?.detail ||
          'Something went wrong. Please email me directly.'
      );
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const isBusy = status === 'loading' || status === 'success';

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.645, 0.045, 0.355, 1],
      },
    },
  };

  return (
    <section className="contact-section" id="contact">
      <div className="section-container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="contact-content"
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="section-header">
            <h2 className="section-title">Let's connect</h2>
            <p className="section-subtitle">
              I welcome conversations about platform engineering, cloud-native infrastructure,
              automation and reliable delivery practices.
            </p>
          </motion.div>

          <div className="contact-layout">
            {/* Contact Info */}
            <motion.div variants={itemVariants} className="contact-info">
              <div className="contact-card">
                <Mail className="contact-icon" size={24} />
                <div>
                  <h3 className="contact-label">Email</h3>
                  <a href={`mailto:${profileData.email}`} className="contact-value">
                    {profileData.email}
                  </a>
                </div>
              </div>

              <div className="contact-card">
                <Github className="contact-icon" size={24} />
                <div>
                  <h3 className="contact-label">GitHub</h3>
                  <a
                    href={profileData.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-value"
                  >
                    @secopsarch
                  </a>
                </div>
              </div>

              <div className="contact-callout">
                <p>
                  The best first note is a short description of the platform, automation or cloud
                  challenge you are working through.
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.form
              variants={itemVariants}
              className="contact-form"
              onSubmit={handleSubmit}
            >
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-input"
                  required
                  disabled={isBusy}
                  data-testid="contact-name-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  required
                  disabled={isBusy}
                  data-testid="contact-email-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="form-textarea"
                  rows="6"
                  required
                  disabled={isBusy}
                  data-testid="contact-message-input"
                />
              </div>

              {status === 'error' && (
                <div className="form-error" role="alert" data-testid="contact-error">
                  <AlertCircle size={16} />
                  {errorMsg}
                </div>
              )}

              <motion.button
                type="submit"
                className="form-submit"
                disabled={isBusy}
                whileHover={!isBusy ? { scale: 1.02, y: -2 } : {}}
                whileTap={!isBusy ? { scale: 0.98 } : {}}
                data-testid="contact-submit-button"
                aria-live="polite"
              >
                {status === 'loading' && (
                  <>
                    <Loader2 size={18} className="spin" />
                    Sending...
                  </>
                )}
                {status === 'success' && (
                  <>
                    <CheckCircle2 size={18} />
                    Message Sent!
                  </>
                )}
                {(status === 'idle' || status === 'error') && (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </motion.button>
            </motion.form>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
